import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Sprout, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function FarmLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role === 'admin') { navigate('/admin'); }
      else if (u.role === 'farm_partner') { navigate('/farm/dashboard'); }
      else {
        await logout();
        toast.error('This is a customer account. Please use Customer Login.');
        navigate('/login');
        return;
      }
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-cream flex items-center justify-center" data-testid="farm-login-page">
      <div className="w-full max-w-md px-6 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-forest rounded-full mb-4"><Sprout className="w-6 h-6 text-cream" /></div>
          <h1 className="font-playfair text-3xl font-bold text-dark">Farm Partner Login</h1>
          <p className="font-manrope text-dark/60 mt-2">Access your FarmBox supplier dashboard</p>
        </div>
        <div className="bg-white rounded-2xl p-8 border border-forest/10 shadow-card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label>Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dark/40" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="farm@example.com" className="pl-12 bg-cream border-forest/20" data-testid="farm-login-email" required />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Password</Label>
                <Link to="/forgot-password" className="text-xs text-forest font-manrope hover:underline" data-testid="farm-forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dark/40" />
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="pl-12 pr-12 bg-cream border-forest/20" data-testid="farm-login-password" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-dark/40 hover:text-dark">{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="farm-login-submit">{loading ? 'Signing in…' : 'Sign In'}</button>
          </form>
        </div>
        <p className="text-center mt-6 font-manrope text-dark/60">No account yet?{' '}<Link to="/farm/apply" className="text-forest font-medium hover:underline" data-testid="apply-link">Apply to Become a FarmBox Partner</Link></p>
        <p className="text-center mt-2 font-manrope text-sm text-dark/50">Shopping with FarmBox?{' '}<Link to="/login" className="text-forest hover:underline">Customer Login</Link></p>
      </div>
    </div>
  );
}
